import { useState, useRef, useEffect, useCallback } from 'react'
import { useUpdateConceptualLabelDiscoveryMutation } from '../../../../../store/apis/conceptual-label.api'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import _ from 'lodash'
import { GROUPING_CAP_TYPES } from '../../../../../constants/grouping-cap-types'

export const usePeriodicSaving = (form, patterns, intervalMs = 15000, getPatternDataFn) => {
    const [isSaving, setIsSaving] = useState(false)
    const [updateConceptualLabelDiscovery] = useUpdateConceptualLabelDiscoveryMutation()

    // Store previous form values and patterns state for comparison
    const previousStateRef = useRef({
        formValues: {},
        patterns: [],
    })
    // Track which patterns are currently being updated
    const updatingPatternsRef = useRef(new Set())

    // Build baseline form values for all patterns, even if their fields are not mounted yet (tabs)
    const buildInitialFormValues = (patternsList = []) => {
        const values = {}
        for (const p of patternsList) {
            const id = p.id
            // Mirror initialValue logic from FormContent
            const hitFeedback = p.feedback ?? p.hit_feedback ?? ''
            const missFeedback = p.miss_feedback ?? ''
            const showHitReasoning = p.show_hit_reasoning || false
            const showMissReasoning = p.show_miss_reasoning || false
            const conceptualLabels = p.proposed_conceptual_labels ? undefined : p.conceptual_labels || []

            values[`name-${id}`] = p.name
            values[`description-${id}`] = p.description
            values[`hit-feedback-${id}`] = hitFeedback
            values[`miss-feedback-${id}`] = missFeedback
            values[`show-hit-reasoning-${id}`] = showHitReasoning
            values[`show-miss-reasoning-${id}`] = showMissReasoning
            values[`color-${id}`] = p.color_id
            values[`warning-level-${id}`] = p.warning_level
            values[`grade-${id}`] = p.grade ?? 0
            values[`grouping-cap-${id}`] = p.grouping_cap ?? GROUPING_CAP_TYPES.ONE.value
            values[`cl-${id}`] = conceptualLabels
        }
        return values
    }

    // Function to save a specific pattern
    const savePattern = useCallback(
        async patternId => {
            // Skip if this pattern is already being updated
            if (updatingPatternsRef.current.has(patternId)) return

            try {
                updatingPatternsRef.current.add(patternId)
                const patternData = getPatternDataFn(patternId)
                if (patternData) {
                    await updateConceptualLabelDiscovery(patternData)
                }
            } catch (error) {
                console.error(`Error updating pattern ${patternId}:`, error)
            } finally {
                updatingPatternsRef.current.delete(patternId)
            }
        },
        [getPatternDataFn, updateConceptualLabelDiscovery],
    )

    // Function to check and save all changed patterns
    const saveAllChangedPatterns = useCallback(async () => {
        if (!patterns?.patterns?.length) return false

        const currentFormValues = form.getFieldsValue()
        const baselineFormValues = buildInitialFormValues(patterns?.patterns || [])
        const mergedCurrentFormValues = { ...baselineFormValues, ...currentFormValues }
        const hasFormChanges = !_.isEqual(mergedCurrentFormValues, previousStateRef.current.formValues)

        // Check for criteria changes
        const hasCriteriaChanges = !_.isEqual(
            patterns.patterns.map(p => ({ id: p.id, criteria: p.criteria })),
            previousStateRef.current.patterns.map(p => ({ id: p.id, criteria: p.criteria })),
        )

        if (hasFormChanges || hasCriteriaChanges) {
            setIsSaving(true)

            try {
                // Find which patterns have changed
                const changedPatternIds = new Set()

                for (const pattern of patterns.patterns) {
                    const patternId = pattern.id

                    // Check form fields
                    const fieldKeys = [
                        `name-${patternId}`,
                        `description-${patternId}`,
                        `hit-feedback-${patternId}`,
                        `miss-feedback-${patternId}`,
                        `show-hit-reasoning-${patternId}`,
                        `show-miss-reasoning-${patternId}`,
                        `color-${patternId}`,
                        `warning-level-${patternId}`,
                        `grade-${patternId}`,
                        `grouping-cap-${patternId}`,
                        `cl-${patternId}`,
                    ]

                    // Check if any field has changed
                    const hasChangedFields = fieldKeys.some(
                        key => !_.isEqual(mergedCurrentFormValues[key], previousStateRef.current.formValues[key]),
                    )

                    // Find previous pattern to compare criteria
                    const previousPattern = previousStateRef.current.patterns.find(p => p.id === patternId)
                    const hasCriteriaChanged = previousPattern && !_.isEqual(pattern.criteria, previousPattern.criteria)

                    if (hasChangedFields || hasCriteriaChanged) {
                        changedPatternIds.add(patternId)
                    }
                }

                // Save all changed patterns
                if (changedPatternIds.size > 0) {
                    const savePromises = Array.from(changedPatternIds).map(patternId => savePattern(patternId))

                    await Promise.all(savePromises)

                    // Update the previous state reference
                    previousStateRef.current = {
                        formValues: mergedCurrentFormValues,
                        patterns: _.cloneDeep(patterns.patterns),
                    }

                    return true // Changes were saved
                }
            } catch (error) {
                openNotificationWithIcon('error', 'Auto-save failed')
            } finally {
                setIsSaving(false)
            }
        }

        return false // No changes to save
    }, [patterns, form, savePattern])

    // Initialize previous state on mount
    useEffect(() => {
        if (patterns?.patterns?.length) {
            // Add a small timeout to allow initial render; seed with baseline values for all patterns
            const timeoutId = setTimeout(() => {
                const baselineFormValues = buildInitialFormValues(patterns.patterns)
                const currentFormValues = form.getFieldsValue()
                previousStateRef.current = {
                    formValues: { ...baselineFormValues, ...currentFormValues },
                    patterns: _.cloneDeep(patterns.patterns),
                }
            }, 300)

            return () => clearTimeout(timeoutId)
        }
    }, [form, patterns])

    // Periodic auto-save effect
    useEffect(() => {
        const interval = setInterval(async () => {
            // Skip if we're already saving or have no patterns to save
            if (isSaving || !patterns?.patterns?.length) return

            await saveAllChangedPatterns()
        }, intervalMs)

        return () => clearInterval(interval)
    }, [isSaving, patterns, intervalMs, saveAllChangedPatterns])

    return { isSaving, saveAllChangedPatterns }
}
